import { applicationsService } from 'api/applications.service';
import { otpService } from 'api/otp.servic';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { renderField } from 'Utils/renderFields';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import './newApplication.scss';
import { IFormApplicationDTO, ChatMessage } from 'shared/interfaces/formApplication';
import { InternalUser } from 'shared/interfaces/form';
import {
    buildInitialFormValues,
    getSaveErrorNotice,
    IFileValue,
    isEditableStatus,
    saveApplication,
    saveApplicationAsDraft,
} from '../../Utils/newApplicationHelpers';
import { ChatContainer } from './ChatContainer/ChatContainer';
import { IconButton, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useNotification } from 'hooks/useNotification';
import { NotificationSnackbar } from 'components/NotificationSnackbar';
import { ApprovalProgress } from 'components/ApprovalProgress';

const NewApplication = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { notification, showNotification, hideNotification } = useNotification();

    const [application, setApplication] = useState<IFormApplicationDTO | null>(null);
    const [userManager] = useState<InternalUser | null>(null); // External users: no manager from parent; used by saveApplication for approver resolution
    const [formValues, setFormValues] = useState<{ [key: string]: string | number | boolean | IFileValue[] }>({});
    // Read-only unless the applicant may still edit it (draft, or returned for changes).
    const [isSubmitted, setIsSubmitted] = useState(!isEditableStatus(application?.applicationStatus));
    const [isChatOpen, setIsChatOpen] = useState(false);
    // #261: from laptop up the chat is an inline split column; below that it is a full-screen
    // overlay, so it must not auto-open there (it would hide the form on arrival).
    const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), { noSsr: true });

    useEffect(() => {
        if (isDesktop && application?.chatMessages && application?.chatMessages?.length > 0) {
            setIsChatOpen(true);
        }
    }, [application, isDesktop]);

    useEffect(() => {
        const getNewApplication = async () => {
            const sessionData = otpService.getSessionToken(id);
            if (!sessionData || !sessionData.token) {
                navigate(`/new-application/${id}/otp`);
                return;
            }

            try {
                const newApplication = await applicationsService.getById(id);
                setApplication(newApplication);
                if (!newApplication) {
                    navigate('/not-found');
                    return;
                }
                const formFields = buildInitialFormValues(newApplication);
                setFormValues(formFields);
            } catch (error: any) {
                console.error('Error getting application:', error);
                // Expired / invalid OTP session (the service already cleared the token) → re-verify.
                const status = error?.response?.status;
                navigate(status === 401 || status === 403 ? `/new-application/${id}/otp` : '/not-found');
            }
        };
        getNewApplication();
    }, [id, navigate]);

    useEffect(() => {
        if (application) {
            setIsSubmitted(!isEditableStatus(application?.applicationStatus));
        }
    }, [application]);

    const handleFieldsChange = (fieldIndex: number, fieldValue: string | number | boolean | IFileValue[]) => {
        // Use functional update to ensure we have the latest state
        setFormValues((prevFieldsValues) => {
            return {
                ...prevFieldsValues,
                [fieldIndex]: fieldValue,
            };
        });
    };

    // Fixable problems (empty mandatory field, invalid value, not editable) are warnings naming
    // the field; an expired session sends the user back to the OTP page.
    const notifySaveError = (error: unknown, fallbackMessage: string) => {
        const notice = getSaveErrorNotice(error, fallbackMessage);
        showNotification(notice.message, notice.severity);
        if (notice.sessionExpired) {
            otpService.clearSessionToken(id);
            navigate(`/new-application/${id}/otp`);
        }
    };

    const handleSaveApplication = async () => {
        if (!application || !application._id) {
            console.error('Cannot save application: application or application ID is missing');
            showNotification('לא ניתן לשמור את הטופס כרגע. אנא נסה לרענן את הדף.', 'error', {
                onShowDelay: 2000,
            });
            return;
        }

        try {
            const updatedApplication = await saveApplication(application, formValues);
            if (updatedApplication) {
                showNotification('הטופס נשמר בהצלחה!', 'success');
                setApplication(updatedApplication);
                setIsSubmitted(!isEditableStatus(updatedApplication.applicationStatus));
            }
        } catch (error) {
            notifySaveError(error, 'שגיאה בשמירת הטופס. אנא נסה שנית או פנה לתמיכה.');
        }
    };

    const handleSaveAsDraft = async () => {
        try {
            const updatedApplication = await saveApplicationAsDraft(application, formValues);
            if (updatedApplication) {
                showNotification('השינויים נשמרו בהצלחה!', 'success');
                setApplication(updatedApplication);
                setIsSubmitted(!isEditableStatus(updatedApplication.applicationStatus));
            }
        } catch (error) {
            notifySaveError(error, 'שגיאה בשמירת טיוטה. אנא נסה שנית.');
        }
    };

    const handleChatMessagesUpdate = (newMessages: ChatMessage[]) => {
        if (application) {
            setApplication({
                ...application,
                chatMessages: newMessages,
            });
        }
    };

    return (
        <>
            {application && Object.keys(formValues).length > 0 ? (
                <div className="your-application">
                    <div className="your-application-header">
                        <label className="title">{application.formSnapshot.title}</label>
                        {/* <label className="description">{application.form.header}</label> */}
                    </div>

                    {/* #143: approval progress — on top below laptop, a side column from laptop up */}
                    {application.applicationStatus !== 'draft' && !isDesktop && (
                        <ApprovalProgress
                            layout="bar"
                            approvalFlow={application.approvalFlow}
                            totalStages={application.totalStages}
                            applicationStatus={application.applicationStatus}
                        />
                    )}

                    <div className="collapse-chat-container">
                        {application.applicationStatus !== 'draft' && (
                            <div className="chat-toggle-row">
                                <IconButton
                                    size="small"
                                    onClick={() => setIsChatOpen((prev) => !prev)}
                                    aria-label={isChatOpen ? "סגור צ'אט" : "פתח צ'אט"}
                                >
                                    {isChatOpen ? <CloseIcon /> : <ChatBubbleOutlineIcon />}
                                </IconButton>
                                <span className="chat-toggle-label">{`${isChatOpen ? 'סגור' : 'פתח'} צ'אט`}</span>
                            </div>
                        )}

                        <div className="application-container">
                            {application.applicationStatus !== 'draft' && (
                                <div className={`chat-container ${!isChatOpen && 'closed'}`}>
                                    {isChatOpen && !isDesktop && (
                                        <div className="chat-mobile-header">
                                            <span>צ'אט</span>
                                            <IconButton size="small" onClick={() => setIsChatOpen(false)} aria-label="סגור צ'אט">
                                                <CloseIcon />
                                            </IconButton>
                                        </div>
                                    )}
                                    {isChatOpen && (
                                        <ChatContainer
                                            application={application}
                                            applicationId={application._id}
                                            messages={application.chatMessages}
                                            currentUser={application.applicantName}
                                            userManager={userManager}
                                            onMessagesUpdate={handleChatMessagesUpdate}
                                        />
                                    )}
                                </div>
                            )}

                            <div
                                className={`content-container ${isChatOpen ? '' : 'expanded'}`}
                                style={{ marginTop: application.applicationStatus === 'draft' ? '10px' : '0px' }}
                            >
                                {application.applicationFields.map((field) => {
                                    const fieldValue = formValues[field.fieldIndex];
                                    // The renderField function takes a field, its current value, and a callback to update the value in the state.
                                    // In addition it takes the application ID to use it later
                                    // It returns (=renders) the corresponding input, with the value bound to the related value in the state.
                                    // The callback executes on input change and it trigers 'handleFieldsChange' with the inputs index and its new value,
                                    // than this function updates the state.

                                    // Debug logging for field data
                                    // console.log('🔍 newApplication - Rendering field with data:', {
                                    //     fieldIndex: field.fieldIndex,
                                    //     fieldType: field.type,
                                    //     applicationId: application._id,
                                    //     applicationFormCategory: application.form.category,
                                    //     applicationFormId: application.form._id,
                                    // });

                                    return (
                                        <React.Fragment key={field.fieldIndex}>
                                            {renderField(
                                                field,
                                                fieldValue,
                                                handleFieldsChange,
                                                application._id,
                                                application.formSnapshot.category,
                                                isSubmitted,
                                                application.formSnapshot._id
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            {application.applicationStatus !== 'draft' && isDesktop && (
                                <div className="approval-flow-sidebar">
                                    <ApprovalProgress
                                        layout="sidebar"
                                        approvalFlow={application.approvalFlow}
                                        totalStages={application.totalStages}
                                        applicationStatus={application.applicationStatus}
                                    />
                                </div>
                            )}
                        </div>
                        {!isSubmitted && (
                            <div className="your-application-buttons">
                                <button type="button" className="draftButton" onClick={handleSaveAsDraft}>
                                    <SaveAsIcon sx={{ fontSize: '22px', marginLeft: '6px' }} />
                                    שמור כטיוטה
                                </button>
                                <button type="submit" className="submitButton" onClick={handleSaveApplication}>
                                    <ThumbUpOffAltIcon sx={{ fontSize: '22px', marginLeft: '6px' }} />
                                    הגש טופס
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <p>Loading...</p>
            )}

            <NotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={hideNotification}
            />
        </>
    );
};

export default NewApplication;
